import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  BASE_POLICY,
  compilePolicy,
  inferPolicy,
  type FollowChannelManagement,
  type ManagementPolicy,
} from "@app/shared/policy";
import { api, type RuleConfigRow, type RuleConfigWriteBody, type RuleSet } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { pushToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ManagementPolicyCard } from "@/components/ManagementPolicyCard";
import { RuleSetEditor } from "@/components/RuleSetEditor";

/**
 * The management screen, in both places it appears: the global default in Settings and a
 * channel's override on its Rules tab.
 *
 * Two modes over one stored row. **Presets** edits a `ManagementPolicy` and saves
 * `{ policy }`, which the server compiles and stores alongside the rules in one statement.
 * **Advanced** edits the rule set directly and saves `{ rules }`, which nulls `policy` — a
 * null policy IS what Advanced mode means, not a missing value.
 *
 * An existing row opens in Presets whenever it can: `inferPolicy` reads the stored rules back
 * into a policy, so the seeded default and anything the compiler produced land in the card
 * rather than dumping Andy into the raw editor. That is also why M14 needed no data-backfill
 * migration.
 */
export function ManagementPolicyEditor({ scope }: { scope: { kind: "global" } | { kind: "channel"; id: number } }) {
  const queryClient = useQueryClient();
  const queryKey = scope.kind === "global" ? queryKeys.globalRules : queryKeys.channelRules(scope.id);
  const query = useQuery({
    queryKey,
    queryFn: () => (scope.kind === "global" ? api.globalRules() : api.channelRules(scope.id)),
  });

  const [mode, setMode] = useState<"presets" | "advanced" | null>(null);
  const [policy, setPolicy] = useState<ManagementPolicy | null>(null);
  const [rules, setRules] = useState<RuleSet | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState(false);

  useEffect(() => {
    // Init-once, the pattern every form in this app uses: a background refetch — a reconnect,
    // a phone unlock, any remount — must not overwrite an edit in progress.
    if (!query.data || mode !== null) return;
    const config = query.data.config;
    const opened = config.policy ?? inferPolicy(config.rules);
    setPolicy(opened ?? BASE_POLICY);
    setRules(config.rules);
    setMode(opened ? "presets" : "advanced");
  }, [query.data, mode]);

  const save = useMutation({
    mutationFn: (body: RuleConfigWriteBody) =>
      scope.kind === "global" ? api.putGlobalRulesBody(body) : api.putChannelRulesBody(scope.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      pushToast(scope.kind === "global" ? "Global management saved" : "Channel management saved");
    },
    onError: (error: Error) => pushToast(error.message),
  });

  const reset = useMutation({
    mutationFn: () => api.deleteChannelRules(scope.kind === "channel" ? scope.id : 0),
    onSuccess: () => {
      // Everything below is now the global set's, so the local edit state has to go with it.
      setMode(null);
      setPolicy(null);
      setRules(null);
      void queryClient.invalidateQueries({ queryKey });
      pushToast("Override removed — this channel follows the global set again");
    },
  });

  // Error FIRST. A failed query never delivers data, so the init-once effect never runs and
  // `mode` stays null forever — with the loading guard first, a server failure rendered a
  // skeleton that never resolved and this message was unreachable. "Broken" and "loading" must
  // not look the same, for the same reason `lib/api.ts` refuses to let "logged out" and "no
  // data" look the same.
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load the management settings. Nothing has been changed.
      </p>
    );
  }
  if (query.isLoading || mode === null || policy === null || rules === null) {
    return <Skeleton className="h-64 w-full" />;
  }

  const config: RuleConfigRow | undefined = query.data?.config;
  const inherited = query.data && "inherited" in query.data && query.data.inherited === true;

  const toPresets = () => {
    const inferred = inferPolicy(rules);
    if (!inferred) {
      // Nothing is discarded here — the dialog asks first, and the raw rules stay in state
      // until a save actually replaces them.
      setConfirmSwitch(true);
      return;
    }
    setPolicy(inferred);
    setMode("presets");
  };

  const toAdvanced = () => {
    // Seeded with what the preset compiles to, so Advanced starts from what is running rather
    // than from a blank editor.
    setRules(compilePolicy(policy));
    setMode("advanced");
  };

  return (
    <div className="space-y-3">
      {inherited && (
        <p className="text-xs text-muted-foreground">
          This channel has no settings of its own — showing the global ones. Saving creates an override that
          applies to this channel alone.
        </p>
      )}

      {query.data?.policyDrift && (
        <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <span>
            The stored rules no longer match the preset they were built from — someone edited them directly.
            The <b>rules</b> are what runs. Saving from Presets will replace them.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">{mode === "presets" ? "Presets" : "Advanced"}</div>
        <Button variant="outline" size="sm" onClick={mode === "presets" ? toAdvanced : toPresets}>
          {mode === "presets" ? "Edit as raw rules" : "Back to presets"}
        </Button>
      </div>

      {mode === "presets" ? (
        <ManagementPolicyCard value={policy} onChange={setPolicy} disabled={save.isPending} />
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Every trigger and action the engine understands, including the ones no preset uses — entry fill, TP3
            and the stop being hit.
          </p>
          <RuleSetEditor value={rules} onChange={setRules} />
        </>
      )}

      <Button
        className="w-full"
        disabled={save.isPending}
        onClick={() =>
          save.mutate(
            mode === "presets"
              ? { policy, enabled: config?.enabled ?? true }
              : { rules, enabled: config?.enabled ?? true },
          )
        }
      >
        {save.isPending ? "Saving…" : "Save"}
      </Button>

      {scope.kind === "channel" && !inherited && config && (
        <Button
          variant="outline"
          className="w-full"
          disabled={reset.isPending}
          onClick={() => reset.mutate()}
        >
          {reset.isPending ? "Removing…" : "Remove override and follow the global settings"}
        </Button>
      )}

      <Dialog open={confirmSwitch} onOpenChange={setConfirmSwitch}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>These rules do not match any preset</DialogTitle>
            <DialogDescription>
              They use a trigger or a value the presets cannot express. Switching starts from the default preset;
              your rules stay exactly as they are until you press Save.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSwitch(false)}>
              Stay in Advanced
            </Button>
            <Button
              onClick={() => {
                setPolicy(BASE_POLICY);
                setMode("presets");
                setConfirmSwitch(false);
              }}
            >
              Start from the default preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FOLLOW_LABELS: Record<FollowChannelManagement, string> = {
  all: "Do what the channel says — close it, move the stop, cancel it",
  closes_only: "Only obey closes and cancels — ignore its stop and target changes",
  ignore: "Ignore its instructions entirely — my settings manage the trade",
};

/**
 * The channel's own follow-ups: obeyed, half obeyed, or not at all.
 *
 * Its own card, outside the policy one, because it is stored in its own column rather than
 * inside the policy — Advanced mode sets `policy` to null, and a per-channel behaviour switch
 * must not disappear because someone chose the raw editor.
 *
 * It saves by re-sending whatever the row already holds (its policy, or its rules) alongside
 * the new mode, since the write route takes one or the other and never neither.
 */
export function FollowChannelCard({ channelId }: { channelId: number }) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.channelRules(channelId);
  const query = useQuery({ queryKey, queryFn: () => api.channelRules(channelId) });

  const save = useMutation({
    mutationFn: (followChannelManagement: FollowChannelManagement) => {
      const config = query.data!.config;
      // `enabled` is carried through rather than defaulted: this card changes one setting, and
      // silently re-enabling a rule config someone had turned off through the API would be a
      // side effect nobody asked for.
      const common = { followChannelManagement, enabled: config.enabled };
      const body: RuleConfigWriteBody = config.policy
        ? { policy: config.policy, ...common }
        : { rules: config.rules, ...common };
      return api.putChannelRulesBody(channelId, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      pushToast("Saved");
    },
    onError: (error: Error) => pushToast(error.message),
  });

  if (query.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>This channel's own instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Could not load this setting. A card that quietly disappears reads as "this channel has
            no such setting", which is not what happened.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (!query.data) return null;
  const inherited = "inherited" in query.data && query.data.inherited === true;

  return (
    <Card>
      <CardHeader>
        <CardTitle>This channel's own instructions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Select
          value={query.data.config.followChannelManagement}
          disabled={save.isPending}
          onChange={(e) => save.mutate(e.target.value as FollowChannelManagement)}
        >
          {(Object.keys(FOLLOW_LABELS) as FollowChannelManagement[]).map((mode) => (
            <option key={mode} value={mode}>
              {FOLLOW_LABELS[mode]}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          A claimed result — "TP1 hit ✅" — is recorded in every mode. It changes no trade, and it is what the
          channel's reliability score is built from, so ignoring a channel's management never means losing track
          of what it claimed.
        </p>
        {inherited && (
          <p className="text-xs text-muted-foreground">
            Changing this creates a settings override for this channel.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
